'use client';

import { Box, Flex, HStack, Link, Text } from '@chakra-ui/react';
import { ExternalLink } from 'lucide-react';
import { CapabilityFilter } from '@/components/settings/CapabilityCatalog';

export type McpClientId = 'codex' | 'chatgpt' | 'openclaw' | 'claude-code' | 'cursor';

export interface McpClientOption {
  id: McpClientId;
  name: string;
  auth: string;
  detail: string;
  docsUrl: string;
  usesApiKey: boolean;
}

export const MCP_CLIENTS: McpClientOption[] = [
  {
    id: 'codex',
    name: 'Codex',
    auth: 'Attune key',
    detail: 'Add Attune as a Streamable HTTP server in Codex config.toml.',
    docsUrl: 'https://developers.openai.com/codex/mcp/',
    usesApiKey: true,
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    auth: 'OAuth',
    detail: 'Create an MCP app in ChatGPT developer mode and authorize Attune with OAuth.',
    docsUrl:
      'https://help.openai.com/en/articles/12584461-developer-mode-apps-and-full-mcp-connectors-in-chatgpt-beta',
    usesApiKey: false,
  },
  {
    id: 'openclaw',
    name: 'OpenClaw',
    auth: 'Attune key',
    detail: 'Save Attune as a Streamable HTTP server, then probe the connection.',
    docsUrl: 'https://docs.openclaw.ai/cli/mcp',
    usesApiKey: true,
  },
  {
    id: 'claude-code',
    name: 'Claude Code',
    auth: 'Attune key',
    detail: 'Add Attune as a user-scoped remote HTTP server with an authorization header.',
    docsUrl: 'https://code.claude.com/docs/en/mcp',
    usesApiKey: true,
  },
  {
    id: 'cursor',
    name: 'Cursor',
    auth: 'Attune key',
    detail: 'Add Attune to your user MCP configuration with the generated key header.',
    docsUrl: 'https://docs.cursor.com/context/model-context-protocol',
    usesApiKey: true,
  },
];

export function McpClientGuide({
  selectedClientId,
  onSelectClient,
}: {
  selectedClientId: McpClientId;
  onSelectClient: (clientId: McpClientId) => void;
}) {
  const selectedClient =
    MCP_CLIENTS.find(client => client.id === selectedClientId) || MCP_CLIENTS[0];

  return (
    <Box borderBottomWidth="1px" borderColor="ui.border.subtle" pb="3">
      <Text fontSize="sm" fontWeight="600" color="ui.text.primary" mb="3">
        Connect your AI client
      </Text>
      <Flex role="group" aria-label="MCP client" gap="4" overflowX="auto" scrollbarWidth="none">
        {MCP_CLIENTS.map(client => {
          const selected = client.id === selectedClient.id;
          return (
            <CapabilityFilter
              key={client.id}
              active={selected}
              onClick={() => onSelectClient(client.id)}
            >
              {client.name}
            </CapabilityFilter>
          );
        })}
      </Flex>

      <HStack justify="space-between" align="start" gap="4" mt="3">
        <Box minW={0}>
          <Text mt="0.5" fontSize="xs" color="ui.text.secondary">
            {selectedClient.detail}
          </Text>
        </Box>
        <Link
          href={selectedClient.docsUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={`${selectedClient.name} MCP documentation`}
          color="ui.text.tertiary"
          flexShrink={0}
          p="1.5"
          _hover={{ color: 'ui.text.primary' }}
        >
          <ExternalLink size={14} />
        </Link>
      </HStack>
    </Box>
  );
}
